import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AudioLines, Camera, ImagePlus, BrainCircuit, Volume2, Square, Mic,
  Loader2, ArrowLeft, History, CreditCard,
} from "lucide-react";
import { API } from "@/lib/site";

function getDeviceId() {
  let id = localStorage.getItem("vl-device-id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("vl-device-id", id);
  }
  return id;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1600;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85).split(",")[1]);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const BigButton = ({ icon: Icon, label, sub, onClick, disabled, variant = "primary", testid }) => {
  const styles = {
    primary: "bg-[#2563EB] text-white shadow-[0_0_28px_rgba(37,99,235,0.45)] hover:bg-[#1D4ED8]",
    teal: "bg-[#00D4FF] text-[#071A35] shadow-[0_0_28px_rgba(0,212,255,0.4)] hover:bg-white",
    ghost: "border border-[#00D4FF]/35 text-slate-100 hover:bg-[#00D4FF]/10",
  }[variant];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`flex min-h-[72px] w-full items-center gap-4 rounded-3xl px-6 py-4 text-left transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 ${styles}`}
    >
      <Icon className="h-8 w-8 shrink-0" aria-hidden="true" />
      <span>
        <span className="block font-display text-lg font-bold sm:text-xl">{label}</span>
        {sub && <span className="block text-sm opacity-80">{sub}</span>}
      </span>
    </button>
  );
};

export default function LensApp() {
  const [image, setImage] = useState(null); // {base64, preview}
  const [text, setText] = useState("");
  const [explanation, setExplanation] = useState("");
  const [readingId, setReadingId] = useState(null);
  const [busy, setBusy] = useState(""); // "reading" | "explaining" | ""
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [history, setHistory] = useState([]);
  const [portalEmail, setPortalEmail] = useState("");
  const [portalBusy, setPortalBusy] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const deviceId = useRef(getDeviceId());

  const speak = useCallback((content) => {
    if (!content || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(content);
    utter.rate = 0.95;
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(utter);
  }, []);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/app/history/${deviceId.current}`);
      setHistory(data.readings || []);
    } catch { /* history is best-effort */ }
  }, []);

  useEffect(() => {
    loadHistory();
    return () => window.speechSynthesis?.cancel();
  }, [loadHistory]);

  const doRead = useCallback(async (img) => {
    const target = img || image;
    if (!target || busy) return;
    stopSpeaking();
    setBusy("reading");
    setExplanation("");
    try {
      const { data } = await axios.post(`${API}/app/read`, {
        image_base64: target.base64,
        device_id: deviceId.current,
      });
      setText(data.text);
      setReadingId(data.reading_id);
      speak(data.text);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read that image. Try a clearer, well-lit photo.");
    } finally {
      setBusy("");
    }
  }, [image, busy, speak, stopSpeaking, loadHistory]);

  const doExplain = useCallback(async () => {
    if (!text || busy) return;
    stopSpeaking();
    setBusy("explaining");
    try {
      const { data } = await axios.post(`${API}/app/explain`, { text, reading_id: readingId });
      setExplanation(data.explanation);
      speak(data.explanation);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Explain failed. Please try again.");
    } finally {
      setBusy("");
    }
  }, [text, busy, readingId, speak, stopSpeaking]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      const img = { base64, preview: URL.createObjectURL(file) };
      setImage(img);
      setText("");
      setExplanation("");
      setReadingId(null);
      doRead(img);
    } catch {
      toast.error("That image couldn't be loaded. Try a JPEG or PNG photo.");
    }
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice commands need Chrome, Edge, or Safari 14.1+.");
      return;
    }
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = false;
    setListening(true);
    rec.onresult = (ev) => {
      const said = ev.results[0][0].transcript.toLowerCase();
      if (said.includes("explain")) doExplain();
      else if (said.includes("stop")) stopSpeaking();
      else if (said.includes("read")) doRead();
      else toast.info(`Heard: “${said}”. Try “Read this”, “Explain this”, or “Stop”.`);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.start();
  };

  const openPortal = async (e) => {
    e.preventDefault();
    if (portalBusy) return;
    setPortalBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/portal`, {
        email: portalEmail,
        origin_url: window.location.origin,
      });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open subscription management.");
      setPortalBusy(false);
    }
  };

  return (
    <section className="relative min-h-screen px-5 pb-24 pt-10 sm:px-8" data-testid="lens-app" aria-label="VoiceLens AI app">
      <div
        className="pointer-events-none absolute -top-24 left-1/2 h-[380px] w-[720px] -translate-x-1/2 rounded-full bg-[#2563EB]/20 blur-[130px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <a href="/" data-testid="app-home-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-[#00D4FF]">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Home
          </a>
          <span className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#2563EB] to-[#00D4FF]">
              <AudioLines className="h-4.5 w-4.5 text-white" aria-hidden="true" />
            </span>
            <span className="font-display text-lg font-bold text-white">VoiceLens <span className="text-[#00D4FF]">AI</span></span>
          </span>
        </div>

        <h1 className="font-display mt-10 text-3xl font-black tracking-tight text-white sm:text-4xl">
          Point. Scan. <span className="text-glow-gradient">Listen.</span>
        </h1>
        <p className="mt-3 text-base text-slate-300 sm:text-lg">
          Photograph any document, label, menu, or screen — VoiceLens reads it aloud and explains it in plain English.
        </p>

        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="app-camera-input" />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="app-upload-input" />

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <BigButton icon={Camera} label="Scan a Document" sub="Opens your camera" variant="primary" testid="app-scan-button"
            onClick={() => cameraRef.current?.click()} disabled={!!busy} />
          <BigButton icon={ImagePlus} label="Upload a Photo" sub="From your gallery or files" variant="ghost" testid="app-upload-button"
            onClick={() => fileRef.current?.click()} disabled={!!busy} />
        </div>

        {image && (
          <div className="mt-6 overflow-hidden rounded-3xl border border-[#00D4FF]/25" data-testid="app-image-preview">
            <img src={image.preview} alt="Document you selected for reading" className="max-h-64 w-full object-cover" />
          </div>
        )}

        {busy && (
          <div className="mt-6 flex items-center gap-3 rounded-2xl border border-[#00D4FF]/25 bg-[#0C2340]/70 p-5" role="status" data-testid="app-busy-indicator">
            <Loader2 className="h-6 w-6 animate-spin text-[#00D4FF]" aria-hidden="true" />
            <p className="text-lg font-semibold text-white">
              {busy === "reading" ? "Reading your document…" : "Explaining in plain English…"}
            </p>
          </div>
        )}

        {text && !busy && (
          <div className="mt-6 rounded-3xl border border-[#00D4FF]/25 bg-[#0C2340]/70 p-6 sm:p-8" data-testid="app-read-result">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[#00D4FF]">What it says</p>
            <p className="whitespace-pre-wrap text-xl leading-relaxed text-white sm:text-2xl" data-testid="app-read-text">{text}</p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <BigButton icon={BrainCircuit} label="Explain This" sub="Plain-English summary" variant="teal" testid="app-explain-button" onClick={doExplain} />
              {speaking ? (
                <BigButton icon={Square} label="Stop Reading" sub="Silence the voice" variant="ghost" testid="app-stop-button" onClick={stopSpeaking} />
              ) : (
                <BigButton icon={Volume2} label="Read Aloud" sub="Hear it again" variant="ghost" testid="app-read-aloud-button" onClick={() => speak(explanation || text)} />
              )}
            </div>
          </div>
        )}

        {explanation && !busy && (
          <div className="mt-6 rounded-3xl border border-emerald-400/30 bg-emerald-400/8 p-6 sm:p-8" data-testid="app-explain-result">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-emerald-300">In plain English</p>
            <p className="text-xl leading-relaxed text-white sm:text-2xl" data-testid="app-explain-text">{explanation}</p>
          </div>
        )}

        <div className="mt-8">
          <BigButton
            icon={Mic}
            label={listening ? "Listening…" : "Voice Command"}
            sub='Say "Read this", "Explain this", or "Stop"'
            variant="ghost"
            testid="app-voice-button"
            onClick={startVoice}
            disabled={!!busy || listening}
          />
        </div>

        {history.length > 0 && (
          <div className="mt-12" data-testid="app-history">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-white">
              <History className="h-5 w-5 text-[#00D4FF]" aria-hidden="true" /> Recent readings
            </h2>
            <ul className="mt-4 space-y-3">
              {history.slice(0, 5).map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    data-testid={`app-history-item-${r.id.slice(0, 8)}`}
                    onClick={() => { setText(r.text); setExplanation(r.explanation || ""); setReadingId(r.id); setImage(null); }}
                    className="w-full rounded-2xl border border-slate-500/25 bg-[#0C2340]/50 p-4 text-left text-base text-slate-200 transition-colors hover:border-[#00D4FF]/40"
                  >
                    {r.text.slice(0, 90)}{r.text.length > 90 ? "…" : ""}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-12 rounded-3xl border border-slate-500/25 bg-[#0C2340]/50 p-6" data-testid="app-portal-card">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-white">
            <CreditCard className="h-5 w-5 text-[#00D4FF]" aria-hidden="true" /> Manage your Founder subscription
          </h2>
          <p className="mt-2 text-sm text-slate-400">Enter the email you used at checkout to update your card or cancel — handled securely by Stripe.</p>
          <form onSubmit={openPortal} className="mt-4 flex flex-col gap-3 sm:flex-row">
            <label htmlFor="portal-email" className="sr-only">Subscription email</label>
            <input
              id="portal-email"
              type="email"
              required
              value={portalEmail}
              onChange={(e) => setPortalEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="app-portal-email-input"
              className="min-h-[56px] flex-1 rounded-full border border-[#00D4FF]/25 bg-[#050F1F] px-6 text-base text-white placeholder:text-slate-500 focus:border-[#00D4FF]"
            />
            <button
              type="submit"
              disabled={portalBusy}
              data-testid="app-portal-button"
              className="min-h-[56px] rounded-full bg-[#00D4FF] px-8 text-base font-bold text-[#071A35] transition-all duration-300 hover:bg-white disabled:opacity-60"
            >
              {portalBusy ? "Opening…" : "Manage Subscription"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
